import React, { useEffect, useState } from 'react';

import apiClient from '../../services/api';
import { SocialAccount } from '../../types/api';
import ConnectAccountModal, { ConnectablePlatform } from './ConnectAccountModal';
import './AccountWizard.css';

type ProviderStatus = Record<string, boolean>;

interface PlatformLauncher {
  id: ConnectablePlatform;
  label: string;
  /** Single-character glyph rendered in the brand-colored disc. Kept
   *  ASCII for now; swap for SVGs when we have proper marks. */
  glyph: string;
  /** Token name on the brand-color CSS variable (see tokens.css). */
  brandToken: string;
  /** OAuth-driven platforms gate on a server-side configuration check.
   *  Bluesky uses an app password so it's always available. */
  oauth: boolean;
}

const LAUNCHERS: PlatformLauncher[] = [
  { id: 'bluesky',   label: 'Bluesky',   glyph: 'B',  brandToken: '--platform-bluesky',   oauth: false },
  { id: 'instagram', label: 'Instagram', glyph: 'IG', brandToken: '--platform-instagram', oauth: true  },
  { id: 'facebook',  label: 'Facebook',  glyph: 'f',  brandToken: '--platform-facebook',  oauth: true  },
  { id: 'tiktok',    label: 'TikTok',    glyph: 'TT', brandToken: '--platform-tiktok',    oauth: true  },
  { id: 'reddit',    label: 'Reddit',    glyph: 'R',  brandToken: '--platform-reddit',    oauth: true  },
];

interface AccountWizardProps {
  onConnected?: (account: SocialAccount) => void;
}

/**
 * Launcher grid for the Accounts page. Each platform is a brand-colored
 * disc with a glyph + name; clicking opens a per-platform connect
 * modal (form for Bluesky, walkthrough → OAuth redirect for the
 * others). The grid replaces the older tabs-and-form picker so the
 * page reads as an action menu instead of a single forced flow.
 */
export default function AccountWizard({ onConnected }: AccountWizardProps) {
  const [providers, setProviders] = useState<ProviderStatus>({});
  const [openPlatform, setOpenPlatform] = useState<ConnectablePlatform | null>(null);

  useEffect(() => {
    apiClient.get<ProviderStatus>('/accounts/oauth/providers/')
      .then(setProviders)
      .catch(() => setProviders({}));
  }, []);

  function isConfigured(p: PlatformLauncher): boolean {
    if (!p.oauth) return true;
    return Boolean(providers[p.id]);
  }

  return (
    <section className="account-wizard">
      <h3>Connect a social account</h3>
      <div className="platform-launcher-grid">
        {LAUNCHERS.map((p) => {
          const configured = isConfigured(p);
          return (
            <button
              key={p.id}
              type="button"
              className={`platform-launcher${configured ? '' : ' is-unconfigured'}`}
              onClick={() => setOpenPlatform(p.id)}
              style={{ '--brand': `var(${p.brandToken})` } as React.CSSProperties}
              title={configured ? `Connect ${p.label}` : `${p.label} — server not configured`}
            >
              <span className="platform-launcher-disc" aria-hidden>{p.glyph}</span>
              <span className="platform-launcher-label">{p.label}</span>
              {!configured && (
                <span className="platform-launcher-note muted">not configured</span>
              )}
            </button>
          );
        })}
      </div>

      {openPlatform && (
        <ConnectAccountModal
          platform={openPlatform}
          configured={isConfigured(LAUNCHERS.find((l) => l.id === openPlatform)!)}
          onClose={() => setOpenPlatform(null)}
          onConnected={(account) => {
            onConnected?.(account);
            setOpenPlatform(null);
          }}
        />
      )}
    </section>
  );
}
